import React, { useState } from "react";
import {
    Box,
    IconButton,
    Avatar,
    Tooltip,
    Typography,
    Menu,
    MenuItem,
    BottomNavigation,
    BottomNavigationAction,
    useMediaQuery,
} from "@mui/material";
import SpaIcon from "@mui/icons-material/Spa";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useNavigate } from "react-router-dom";
import { emphasize } from '@mui/material/styles';
import { useUserAuth } from '../context/UserAuthContext';
// Import the logo as a React component or image
import Logo from '../assets/headerlogo.png'; // Adjust path as needed

const secondaryColor = '#F9AA33';

const FarmSideBar = ({
    onLogout,
    idToken,
    userData = {},
    farm_id,
    zones = [],
    farmLocations = [],
}) => {
    const [anchorElUser, setAnchorElUser] = useState(null);
    const navigate = useNavigate();
    const { user } = useUserAuth();
    const isMobile = useMediaQuery("(max-width: 600px)");

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleCopyIdToken = async () => {
        try {
            await navigator.clipboard.writeText(`Bearer ${idToken}`);
            alert("ID Token copied to clipboard");
        } catch (err) {
            console.error("Failed to copy ID token:", err);
        }
    };

    const handleNavigation = (path) => {
        if (farm_id) {
            navigate(path, {
                state: { idToken, farmLocations, userData, zones, farm_id },
            });
        } else {
            console.error("Farm ID is undefined. Navigation canceled.");
        }
    };

    // Use userData if available, otherwise fall back to Firebase user
    const displayUserData = userData || user || {};
    const avatarLetter = displayUserData?.name?.[0]?.toUpperCase() || 
                        displayUserData?.displayName?.[0]?.toUpperCase() || 
                        displayUserData?.phoneNumber?.[0]?.toUpperCase() || 
                        "?";

    // Sidebar navigation items
    const navItems = [
        { icon: <BarChartIcon />, label: "Dashboard", path: `/farm/${farm_id}` },
        { icon: <SpaIcon />, label: "Crops", path: `/crops/${farm_id}` },
        { icon: <AttachMoneyIcon />, label: "Finance", path: `/financials/${farm_id}` },
        { icon: <ContentPasteSearchIcon />, label: "Traceability", path: `/traceability/${farm_id}` },
    ];

    // Determine selected index based on current path
    const currentPath = window.location.pathname;
    const selectedIdx = navItems.findIndex(item => currentPath.startsWith(item.path.split('/').slice(0,3).join('/')));

    if (isMobile) {
        // Render Bottom Navigation on mobile devices
        return (
            <Box sx={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 1000, bgcolor: "#181F2A", display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, py: 1 }}>
                {/* All navigation icons and avatar in one row with even spacing */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', maxWidth: 400 }}>
                    {navItems.map((item, idx) => (
                        <IconButton
                            key={item.label}
                            onClick={() => handleNavigation(item.path)}
                            sx={{ 
                                color: selectedIdx === idx ? secondaryColor : '#fff',
                                p: 1
                            }}
                        >
                            {item.icon}
                        </IconButton>
                    ))}
                    {/* Avatar as the 5th element */}
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 1 }}>
                        <Avatar alt="User Avatar" sx={{ bgcolor: secondaryColor, color: '#fff', width: 24, height: 24, fontSize: '0.75rem' }}>{avatarLetter}</Avatar>
                    </IconButton>
                </Box>
                {/* Menu for Avatar */}
                <Menu
                    anchorEl={anchorElUser}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    keepMounted
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                >
                    <MenuItem>{displayUserData.name || displayUserData.displayName || displayUserData.phoneNumber || 'User'}</MenuItem>
                    <MenuItem
                        onClick={() => {
                            handleCopyIdToken();
                            handleCloseUserMenu();
                        }}
                    >
                        <Typography textAlign="center">Copy ID Token</Typography>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            onLogout();
                            handleCloseUserMenu();
                        }}
                    >
                        <Typography textAlign="center">Logout</Typography>
                    </MenuItem>
                </Menu>
            </Box>
        );
    }

    // Custom sidebar for desktop
    return (
        <Box
            sx={{
                backgroundColor: '#181F2A',
                width: '70px',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'fixed',
                left: 0,
                top: 0,
                zIndex: 1200,
                pt: 2,
            }}
        >
            {/* Logo at the top, not clickable */}
            <Box
                sx={{
                    width: 30,
                    height: 30,
                    mt: 1,
                    mb: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <img src={Logo} alt="Logo" style={{ width: '100%', height: '100%' }} />
            </Box>

            {/* Page Icons - upper-middle */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    gap: 3,
                    mt: 26,
                }}
            >
                {navItems.map((item, idx) => (
                    <Tooltip title={item.label} placement="right" key={item.label}>
                        <IconButton
                            color="inherit"
                            aria-label={item.label}
                            onClick={() => handleNavigation(item.path)}
                            sx={{
                                color: selectedIdx === idx ? secondaryColor : '#fff',
                                mb: 1,
                                width: 40,
                                height: 40,
                                mx: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: selectedIdx === idx ? emphasize('#181F2A', 0.12) : 'none',
                                borderRadius: '50%',
                                transition: 'background 0.2s',
                            }}
                        >
                            {item.icon}
                        </IconButton>
                    </Tooltip>
                ))}
            </Box>

            {/* User Avatar at the bottom */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    position: 'absolute',
                    bottom: 16,
                }}
            >
                <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                        <Avatar alt="User Avatar" sx={{ bgcolor: secondaryColor, color: '#fff' }}>{avatarLetter}</Avatar>
                    </IconButton>
                </Tooltip>
            </Box>
            {/* Menu for Avatar - Opens upwards */}
            <Menu
                sx={{ mt: '-45px' }}
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                keepMounted
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <MenuItem>{displayUserData.name || displayUserData.displayName || displayUserData.phoneNumber || 'User'}</MenuItem>
                <MenuItem
                    onClick={() => {
                        handleCopyIdToken();
                        handleCloseUserMenu();
                    }}
                >
                    <Typography textAlign="center">Copy ID Token</Typography>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onLogout();
                        handleCloseUserMenu();
                    }}
                >
                    <Typography textAlign="center">Logout</Typography>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default FarmSideBar;
